/**
 * Property Model — pure types + pure helpers for the top-level `properties/`
 * collection (AD-013, Module 4).
 *
 * A Property record is the permanent, reusable store for a physical address,
 * its occupants (FK to contacts/), its floor list, and its engagement refs
 * (FK to projects/). Nothing premises-level lives on a project or intake —
 * intakes reference Properties via propertyId.
 *
 * NO Firebase imports — pure, unit-testable.
 */

// ─── Property ID format ───────────────────────────────────────────────────

/** Mint a P##### id from a sequence number (zero-padded to 5 digits). */
export function propertyNoFromSeq(seq: number): string {
  return `P${String(seq).padStart(5, '0')}`;
}

// ─── Floor IDs (canonical ordered set, basement-up) ──────────────────────

export const FLOOR_IDS = ['B2', 'B1', 'GF', 'FF', 'SF', 'TF', 'RF'] as const;
export type FloorId = (typeof FLOOR_IDS)[number];

export const FLOOR_LABELS: Record<FloorId, string> = {
  B2: 'Basement 2',
  B1: 'Basement 1',
  GF: 'Ground Floor',
  FF: 'First Floor',
  SF: 'Second Floor',
  TF: 'Third Floor',
  RF: 'Roof / Terrace',
};

/**
 * PURE — return the human label for a floor id.
 * Unknown ids pass through unchanged (graceful unknown).
 */
export function floorLabel(floorId: string): string {
  return FLOOR_LABELS[floorId as FloorId] ?? floorId;
}

/** Type guard — is this string a canonical FloorId? */
export function isFloorId(s: string): s is FloorId {
  return FLOOR_IDS.includes(s as FloorId);
}

/**
 * PURE — best-effort map a free intake floor LABEL (e.g. "Ground Floor",
 * "First", "GF", "Basement 1") to a canonical FloorId. Falls back to GF when
 * nothing matches (a floor must carry SOME id). Used by the intake → property
 * drawing-set write-through (AD-040), where the intake stores a label string.
 */
export function floorIdFromLabel(label: string): FloorId {
  const s = (label || '').trim().toLowerCase();
  if (!s) return 'GF';
  // Direct id match (e.g. "GF", "FF").
  const up = s.toUpperCase();
  if (isFloorId(up)) return up;
  // Canonical label match.
  for (const id of FLOOR_IDS) {
    if (FLOOR_LABELS[id].toLowerCase() === s) return id;
  }
  // Keyword heuristics.
  if (/basement\s*2|b2|lower\s*basement/.test(s)) return 'B2';
  if (/basement\s*1|basement|b1/.test(s)) return 'B1';
  if (/ground|gf|\bg\b/.test(s)) return 'GF';
  if (/first|1st|ff|\bf1\b/.test(s)) return 'FF';
  if (/second|2nd|sf|\bf2\b/.test(s)) return 'SF';
  if (/third|3rd|tf|\bf3\b/.test(s)) return 'TF';
  if (/roof|terrace|rf|penthouse/.test(s)) return 'RF';
  return 'GF';
}

// ─── Project type options ─────────────────────────────────────────────────

export type ProjectType =
  | 'residence'
  | 'office'
  | 'commercial'
  | 'retail'
  | 'industrial'
  | 'institutional';

export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  residence: 'Residence',
  office: 'Office',
  commercial: 'Commercial',
  retail: 'Retail',
  industrial: 'Industrial',
  institutional: 'Institutional',
};

// ─── Occupant entry — FK to contacts/ ────────────────────────────────────

export type OccupantRole = 'owner' | 'tenant' | 'occupant' | 'business';

export interface OccupantEntry {
  contactId: string;
  role: OccupantRole;
  /** e.g. "primary occupant", "landlord" */
  relationship?: string;
}

// ─── Engagement reference — FK to projects/ ──────────────────────────────

export interface PropertyEngagementRef {
  projectId: string;       // "VP675"
  role: 'primary' | 'candidate';
  label?: string;          // "Property A" (L1A multi-candidate only)
  addedAt: string;         // ISO timestamp
}

// ─── Client reference — FK to clients/ (AD-036) ──────────────────────────

/**
 * AD-036 — a client a property belongs to. Added so a client's properties are
 * queryable even before a project exists (intake is client-scoped). A building
 * can serve multiple clients, so this is an array (clientRefs[]) — parallel to
 * occupants[] (contacts) and engagementRefs[] (projects).
 */
export interface PropertyClientRef {
  clientId: string;        // "C00001"
  addedAt: string;         // ISO timestamp
}

/**
 * PURE — add a client ref to a property's clientRefs[], de-duplicating by
 * clientId (an existing entry's addedAt is preserved). Returns a NEW array;
 * never mutates the input.
 */
export function addClientRef(
  refs: PropertyClientRef[] | undefined,
  clientId: string,
  addedAt: string = new Date().toISOString(),
): PropertyClientRef[] {
  const list = refs ?? [];
  if (list.some((r) => r.clientId === clientId)) return list;
  return [...list, { clientId, addedAt }];
}

// ─── Floor plan version ───────────────────────────────────────────────────

/** One uploaded floor-plan image version on a PropertyFloor. */
export interface FloorPlanVersion {
  /** Unique id for this version (timestamp-based: e.g. `v_<epoch>`). */
  planId: string;
  /** Firebase Storage download URL. */
  imageUrl: string;
  /** Optional thumbnail URL (smaller version for list view). */
  thumbUrl?: string;
  /** ISO timestamp of upload. */
  uploadedAt: string;
  /** Storage path (for future re-fetch / replace). */
  storagePath: string;
  /** Content type (e.g. `image/jpeg`). */
  contentType: string;
  /** Natural pixel width of the uploaded image (tracer coordinate space). */
  width: number;
  /** Natural pixel height. */
  height: number;
  /** Is this the currently active version shown to users? */
  isActive: boolean;
  /**
   * AD-036 — optional human-readable stage label for the version timeline
   * (e.g. "Concept v1", "Concept v2", "Developed"). Makes the per-floor
   * Properties-manager timeline meaningful instead of bare timestamps.
   * Linear history INSIDE a concept; distinct from the parallel DesignConcept layer.
   */
  stage?: string;
}

// ─── Point (shared with vastuGrid; defined here to keep model pure) ──────

/** A 2-D point in image-pixel space. */
export interface Pt2D {
  x: number;
  y: number;
}

// ─── Floor entry (Phase 2 — plans, boundary, grid) ───────────────────────

/**
 * A floor on a Property. Phase-2+ shape. Back-compat: the Phase-1 shape was
 * `{ floorId, label, areaApprox? }` — this is a strict superset.
 */
export interface PropertyFloor {
  floorId: FloorId;
  label: string;
  areaApprox?: number;     // sq ft, optional
  /** Uploaded floor-plan images — multiple versions; only the `isActive` one is used. */
  plans?: FloorPlanVersion[];
  /**
   * Traced boundary polygon in IMAGE-PIXEL space (same coordinate space as
   * `vastuGrid.ts` / `FloorBoundaryTracer`). ≥3 pts required to compute a grid.
   */
  boundary?: Pt2D[];
  /**
   * Raw boundary (before the `cleanBoundary` pass). Kept for round-trip
   * fidelity; the engine uses `boundary` (the cleaned version).
   */
  boundaryRaw?: Pt2D[];
  /** Computed grid reference id (written after the grid is confirmed). */
  gridId?: string;
  /**
   * Per-floor north override (overrides the property-level `northRotation`).
   * Most floors inherit from the property; set only when a floor deviates.
   */
  northRotation?: number;
  /**
   * AD-038 — soft-delete flag. A deleted floor is NEVER spliced from the
   * version's floors[]; it is flagged + filtered by `versionFloors()`. The
   * Undo toast clears it; Trash restores/purges it. `deletedAt` is ISO.
   */
  deleted?: boolean;
  deletedAt?: string;
}

// ─── Site location (3-box model, AD-013 revision) ────────────────────────

/**
 * New 3-box location model. Replaces the old SiteLocation (lat/lon/google_maps_url/source).
 *
 * Box 2: mapsUrl — raw Google Maps link pasted by staff.
 * Box 3: lat/lng — always stored as decimal degrees (converted from DMS on entry).
 *        coordFormat records which format was entered.
 *        dms holds the raw DMS values when coordFormat === 'dms'.
 */
export interface PropertySiteLocation {
  mapsUrl?: string;           // Google Maps link (Box 2)
  lat?: number;               // decimal northing (always stored as decimal)
  lng?: number;               // decimal easting  (always stored as decimal)
  /** Raw string as entered by staff (for round-trip display). */
  rawCoords?: string;
  coordFormat?: 'decimal' | 'dms';
  dms?: {
    northDeg: number; northMin: number; northSec: number; northDir: 'N' | 'S';
    eastDeg:  number; eastMin:  number; eastSec:  number; eastDir:  'E' | 'W';
  };
}

// ─── Full Property doc shape ──────────────────────────────────────────────

/**
 * The Google-Earth north screenshot stored on a Property doc.
 * Uses camelCase (consistent with PropertySiteLocation) rather than the
 * snake_case used on the CLIENT intake (NorthScreenshot in floorPlan.ts).
 */
export interface PropertyNorthScreenshot {
  storagePath: string;
  downloadUrl: string;
  uploadedAt: string;       // ISO timestamp
  width?: number;
  height?: number;
}

export interface LiveProperty {
  propertyId: string;      // "P00001"
  /** Full human-readable address (optional — Box 2 or Box 3 is mandatory instead). */
  address?: string;
  addressComps?: {
    street?: string;
    city?: string;
    state?: string;
    pin?: string;
    country?: string;
  };
  siteLocation?: PropertySiteLocation;
  projectType?: ProjectType;
  /** 0–359.9°, staff-entered north bearing */
  northRotation?: number;
  /**
   * Google-Earth north screenshot — durable ref for the report and
   * future reference. Null / absent = no screenshot yet.
   */
  northScreenshot?: PropertyNorthScreenshot | null;
  occupants?: OccupantEntry[];
  /**
   * Floor records. Phase 1: `{ floorId, label, areaApprox? }`.
   * Phase 2+: `PropertyFloor` with optional `plans[]`, `boundary`, `gridId`.
   * Back-compat: the Phase-1 minimal shape is a valid PropertyFloor.
   */
  floors: PropertyFloor[];
  engagementRefs: PropertyEngagementRef[];
  /**
   * AD-036 — clients this property belongs to. Optional/back-compat: absent on
   * pre-AD-036 property docs (treat as []). A building can serve many clients.
   */
  clientRefs?: PropertyClientRef[];
  /**
   * AD-036 — parallel architect design options for THIS building (Concept A /
   * Concept B). Optional/back-compat: absent ⇒ a single implicit "Default"
   * concept (the layer is hidden — simple stays simple). Plan versions/grids
   * hang under a concept once more than one exists. NOT separate properties.
   */
  concepts?: DesignConcept[];
  createdAt: string;       // ISO timestamp
  createdBy: string;       // uid
  updatedAt: string;
  archived: boolean;
  /**
   * AD-038 — recoverable Trash for a WHOLE property (parallel to client/project/
   * partner trashed flags). A trashed property is hidden from every normal list
   * and surfaces only in the Trash view; restore clears the flag. Distinct from
   * `archived` (archive keeps it in scope, just out of the default view).
   * Optional/back-compat: absent ⇒ not trashed.
   */
  trashed?: boolean;
  trashedAt?: string | null;
  trashedBy?: string | null;
}

// ─── Design Concept layer (AD-036) ───────────────────────────────────────

/**
 * AD-036 — a parallel design option for ONE building (the architect's term).
 * The building (location/north/plot) is fixed; concepts are the alternatives
 * (Concept A — Arch X, Concept B). Each concept owns its own linear plan-version
 * history + grid. The report compares concepts; staff mark the selected one.
 *
 * Stored as an array on the property (`concepts[]`) rather than a subcollection:
 * concepts are few (typically 2–3), small (id/name/flags only — the heavy plan
 * images + grids already live in floors[].plans[] / grids subcollection keyed by
 * conceptId), and need to render together with the property in one read. A
 * subcollection would add an N+1 read for no benefit. (Grids stay a subcollection
 * because a single grid blob is 20–80KB; a concept descriptor is a few bytes.)
 */
export interface DesignConcept {
  /** Stable id (e.g. `concept_<epoch>`); the implicit single design = "default". */
  conceptId: string;
  /** Display name — "Concept A", "Concept B", or an architect-given name. */
  name: string;
  /** Exactly one concept may be the client-selected option (after they decide). */
  isSelected: boolean;
  /** ISO timestamp of creation. */
  createdAt: string;
  /**
   * AD-038 — design iterations within this concept. A concept owns one or more
   * versions; exactly one is `isActive`. The floors live UNDER a version (so a
   * floor is auto-tagged to concept+version). Optional/back-compat: a pre-AD-038
   * flat-descriptor concept has no `versions` — `normalizeProperty` synthesizes
   * one (stage Developed, active) and moves the top-level floors[] into it.
   */
  versions?: DesignVersion[];
  /**
   * AD-038 — soft-delete flag (never splice). Filtered by `liveConcepts()` /
   * `activeConcept()`. `deletedAt` is ISO.
   */
  deleted?: boolean;
  deletedAt?: string;
  /**
   * AD-040 (2026-06-24) — provenance for a concept that arrived from an intake
   * drawing-set write-through. Absent on concepts created directly on the
   * Property page or by the migration. Lets the Property page show
   * "from L1C intake · <tier> · <date>".
   */
  source?: ConceptSource;
}

/**
 * AD-040 — where a DesignConcept came from. Today: an intake drawing-set
 * write-through (`intake`) or a direct add on the Property page (`property_page`).
 */
export interface ConceptSource {
  kind: 'intake' | 'property_page';
  /** The intake scope id (projectId or clientId) the drawing set came from. */
  sourceIntakeId?: string;
  /** Service tier the intake belonged to (e.g. `L1C`, `L2C_R1`). */
  tier?: string;
  /** ISO timestamp the concept was written through. */
  at: string;
  /** Actor uid (audit). */
  by?: string;
}

/**
 * AD-038 — one design iteration within a DesignConcept. Stage is chosen from a
 * suggested set (Concept/Preliminary/Developed/Revised/Final) — selectable, NOT
 * free-typed per floor (custom strings are allowed as a fallback). Floors of THIS
 * version live here; adding a floor inside a selected concept+version auto-tags it.
 */
export interface DesignVersion {
  /** Stable id (e.g. `version_<epoch>`); the implicit first version = "v1". */
  versionId: string;
  /** Design stage — from DESIGN_STAGES (or a custom string fallback). */
  stage: string;
  /** Exactly one version per concept is active (the one shown / worked in). */
  isActive: boolean;
  /** ISO timestamp of creation. */
  createdAt: string;
  /** The floors of THIS version (B1/GF/FF…); each carries its own plan + grid. */
  floors?: PropertyFloor[];
  /** AD-038 — soft-delete flag (never splice). Filtered by `liveVersions()`. */
  deleted?: boolean;
  deletedAt?: string;
}

/**
 * AD-038 — the suggested design-stage set (selectable, not free-typed per floor).
 * A custom string is still accepted on the model (fallback) but the UI offers
 * these first, in this top-down order.
 */
export const DESIGN_STAGES = [
  'Concept',
  'Preliminary',
  'Developed',
  'Revised',
  'Final',
] as const;
export type DesignStage = (typeof DESIGN_STAGES)[number];

/** The implicit single-design concept id used when a building has one design. */
export const DEFAULT_CONCEPT_ID = 'default';

/** AD-038 — the implicit first version id used by the migration / single design. */
export const DEFAULT_VERSION_ID = 'v1';

/** AD-038 — mint a stable design-version id from the current epoch. */
export function newVersionId(): string {
  return `version_${Date.now()}`;
}

/**
 * PURE — mint a design-concept id from the current epoch.
 * e.g. `concept_1718700000000`.
 */
export function newConceptId(): string {
  return `concept_${Date.now()}`;
}

/**
 * PURE — should the Design-Concept layer be SHOWN in the UI?
 * Hidden when a building has 0 or 1 concept (simple stays simple); shown once
 * there are 2+ parallel options. (The implicit "Default" concept is never an
 * explicit entry, so 0 concepts === single design too.)
 */
export function hasMultipleConcepts(prop: Pick<LiveProperty, 'concepts'>): boolean {
  return (prop.concepts?.length ?? 0) > 1;
}

/**
 * PURE — the selected DesignConcept, or null when none is marked / none exist.
 */
export function selectedConcept(
  concepts: DesignConcept[] | undefined,
): DesignConcept | null {
  const list = concepts ?? [];
  return list.find((c) => c.isSelected === true) ?? null;
}

/**
 * PURE — append a new DesignConcept to the list. Returns a NEW array.
 * The first concept added to an empty list is marked selected by default
 * (a single explicit concept is the de-facto choice); later additions are not.
 */
export function addConcept(
  concepts: DesignConcept[] | undefined,
  name: string,
  conceptId: string = newConceptId(),
  createdAt: string = new Date().toISOString(),
): DesignConcept[] {
  const list = concepts ?? [];
  const isFirst = list.length === 0;
  // AD-038 — a new concept is born with one empty active version (stage
  // "Concept"), so the concept→version→floor hierarchy is never partial.
  const seedVersion: DesignVersion = {
    versionId: DEFAULT_VERSION_ID,
    stage: 'Concept',
    isActive: true,
    createdAt,
    floors: [],
  };
  return [
    ...list,
    { conceptId, name, isSelected: isFirst, createdAt, versions: [seedVersion] },
  ];
}

/**
 * PURE — mark exactly one concept (by id) as selected; clears the flag on all
 * others. Returns a NEW array. A no-op (returns input shape) when the id is
 * absent — callers should guard, but this never throws.
 */
export function selectConcept(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
): DesignConcept[] {
  const list = concepts ?? [];
  return list.map((c) => ({ ...c, isSelected: c.conceptId === conceptId }));
}

// ─── AD-038 — Concept ▸ Version ▸ Floor accessors (PURE) ─────────────────

/**
 * PURE — concepts that are NOT soft-deleted, in stored order.
 * The single source of truth for "what concepts exist" everywhere in the UI.
 */
export function liveConcepts(
  concepts: DesignConcept[] | undefined,
): DesignConcept[] {
  return (concepts ?? []).filter((c) => c.deleted !== true);
}

/**
 * PURE — versions of a concept that are NOT soft-deleted, in stored order.
 */
export function liveVersions(
  concept: Pick<DesignConcept, 'versions'> | null | undefined,
): DesignVersion[] {
  return (concept?.versions ?? []).filter((v) => v.deleted !== true);
}

/**
 * PURE — floors of a version that are NOT soft-deleted, in stored order.
 * The hierarchy's leaf accessor — drives the Floors list in the UI.
 */
export function versionFloors(
  version: Pick<DesignVersion, 'floors'> | null | undefined,
): PropertyFloor[] {
  return (version?.floors ?? []).filter((f) => f.deleted !== true);
}

/**
 * PURE — the "active" concept to work in: the first non-deleted concept marked
 * selected, else the first non-deleted concept, else null. (For single-design
 * buildings there's one implicit Default concept after normalization.)
 */
export function activeConcept(
  prop: Pick<LiveProperty, 'concepts'>,
): DesignConcept | null {
  const live = liveConcepts(prop.concepts);
  if (live.length === 0) return null;
  return live.find((c) => c.isSelected === true) ?? live[0];
}

/**
 * PURE — the active version of a concept: the first non-deleted version flagged
 * isActive, else the first non-deleted version, else null.
 */
export function activeVersion(
  concept: Pick<DesignConcept, 'versions'> | null | undefined,
): DesignVersion | null {
  const live = liveVersions(concept);
  if (live.length === 0) return null;
  return live.find((v) => v.isActive === true) ?? live[0];
}

// ─── AD-038 — PURE forward-migration (idempotent, dual-read) ──────────────

/**
 * PURE, IDEMPOTENT — normalise a property into the Concept ▸ Version ▸ Floor
 * shape. Applied lazily on read (dual-read) AND by the one-shot backfill script.
 *
 * Rules:
 *   · If at least one concept already has a non-empty `versions[]` → the property
 *     is already normalised → returned UNCHANGED (referential no-op, idempotent).
 *   · Otherwise synthesize a single "Default" concept → one "Developed" version
 *     (active) → move the existing top-level `floors[]` into that version.
 *     A pre-existing flat-descriptor concept (name/isSelected, no versions) is
 *     reused for naming when present (its name + selected flag carry over).
 *
 * The top-level `floors[]` is RETAINED untouched (back-compat read); the canonical
 * floors live under `concepts[].versions[].floors[]`. NEVER mutates the input.
 */
export function normalizeProperty<T extends Pick<LiveProperty, 'floors' | 'concepts'>>(
  prop: T,
): T {
  const concepts = prop.concepts ?? [];
  // Already normalised? — any concept carrying a versions[] (even empty) means
  // the hierarchy has been authored; do nothing (idempotent).
  const alreadyVersioned = concepts.some((c) => Array.isArray(c.versions));
  if (alreadyVersioned) return prop;

  const createdAt = new Date().toISOString();
  const topFloors = prop.floors ?? [];
  const version: DesignVersion = {
    versionId: DEFAULT_VERSION_ID,
    stage: 'Developed',
    isActive: true,
    createdAt,
    floors: topFloors.map((f) => ({ ...f })),
  };

  // Reuse a single pre-existing flat concept's identity if present (preserve its
  // name + selected flag); else synthesize the implicit "Default" concept.
  const base = concepts[0];
  const defaultConcept: DesignConcept = base
    ? {
        conceptId: base.conceptId,
        name: base.name,
        isSelected: true,
        createdAt: base.createdAt ?? createdAt,
        versions: [version],
        ...(base.deleted != null ? { deleted: base.deleted } : {}),
        ...(base.deletedAt != null ? { deletedAt: base.deletedAt } : {}),
      }
    : {
        conceptId: DEFAULT_CONCEPT_ID,
        name: 'Default design',
        isSelected: true,
        createdAt,
        versions: [version],
      };

  // If there were multiple flat concepts (rare pre-AD-038), keep the extras but
  // give each its own synthesized empty version so the hierarchy is total.
  const extras = concepts.slice(1).map((c) => ({
    ...c,
    versions: [
      {
        versionId: DEFAULT_VERSION_ID,
        stage: 'Developed',
        isActive: true,
        createdAt: c.createdAt ?? createdAt,
        floors: [],
      } as DesignVersion,
    ],
  }));

  return { ...prop, concepts: [defaultConcept, ...extras] };
}

// ─── AD-038 — Concept / Version / Floor CRUD on the array (PURE) ──────────

/** PURE — rename a concept by id. Returns a NEW array; no-op if absent. */
export function renameConcept(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  name: string,
): DesignConcept[] {
  return (concepts ?? []).map((c) =>
    c.conceptId === conceptId ? { ...c, name } : c,
  );
}

/**
 * PURE — soft-delete a concept by id (set deleted+deletedAt; never splice).
 * GUARD: refuses to soft-delete the LAST live concept (returns input unchanged).
 * When the deleted concept was selected, selection moves to the first remaining
 * live concept.
 */
export function softDeleteConcept(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  deletedAt: string = new Date().toISOString(),
): DesignConcept[] {
  const list = concepts ?? [];
  const live = liveConcepts(list);
  if (live.length <= 1) return list; // never delete the last concept
  const target = live.find((c) => c.conceptId === conceptId);
  if (!target) return list;
  const wasSelected = target.isSelected === true;
  const remaining = live.filter((c) => c.conceptId !== conceptId);
  return list.map((c) => {
    if (c.conceptId === conceptId) return { ...c, deleted: true, deletedAt };
    if (wasSelected && remaining.length > 0)
      return { ...c, isSelected: c.conceptId === remaining[0].conceptId };
    return c;
  });
}

/** PURE — clear the soft-delete flag on a concept (Undo / Trash restore). */
export function restoreConcept(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
): DesignConcept[] {
  return (concepts ?? []).map((c) =>
    c.conceptId === conceptId
      ? (() => {
          const { deleted: _d, deletedAt: _da, ...rest } = c;
          return rest;
        })()
      : c,
  );
}

/** PURE — hard-remove a concept by id (Trash purge). Returns a NEW array. */
export function purgeConcept(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
): DesignConcept[] {
  return (concepts ?? []).filter((c) => c.conceptId !== conceptId);
}

/**
 * PURE — apply a mapping fn to ONE concept's versions[] by conceptId, returning
 * a NEW concepts array. The reusable spine for all version-level edits.
 */
function mapConceptVersions(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  fn: (versions: DesignVersion[]) => DesignVersion[],
): DesignConcept[] {
  return (concepts ?? []).map((c) =>
    c.conceptId === conceptId ? { ...c, versions: fn(c.versions ?? []) } : c,
  );
}

/**
 * PURE — add a version to a concept. The new version becomes active (the prior
 * active is deactivated). `carryFloors` (default true) deep-copies the previous
 * active version's live floors forward (replace only what changed). Returns a
 * NEW concepts array.
 */
export function addVersion(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  stage: string,
  opts: { carryFloors?: boolean; versionId?: string; createdAt?: string } = {},
): DesignConcept[] {
  const carry = opts.carryFloors !== false;
  const versionId = opts.versionId ?? newVersionId();
  const createdAt = opts.createdAt ?? new Date().toISOString();
  return mapConceptVersions(concepts, conceptId, (versions) => {
    const prevActive =
      versions.find((v) => v.isActive && v.deleted !== true) ??
      versions.filter((v) => v.deleted !== true).slice(-1)[0];
    const carriedFloors =
      carry && prevActive
        ? versionFloors(prevActive).map((f) => ({ ...f }))
        : [];
    const deactivated = versions.map((v) =>
      v.isActive ? { ...v, isActive: false } : v,
    );
    return [
      ...deactivated,
      { versionId, stage, isActive: true, createdAt, floors: carriedFloors },
    ];
  });
}

/** PURE — rename a version's stage. Returns a NEW concepts array. */
export function renameVersion(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  versionId: string,
  stage: string,
): DesignConcept[] {
  return mapConceptVersions(concepts, conceptId, (versions) =>
    versions.map((v) => (v.versionId === versionId ? { ...v, stage } : v)),
  );
}

/** PURE — mark exactly one version active within a concept (clears the rest). */
export function selectVersion(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  versionId: string,
): DesignConcept[] {
  return mapConceptVersions(concepts, conceptId, (versions) =>
    versions.map((v) => ({ ...v, isActive: v.versionId === versionId })),
  );
}

/**
 * PURE — soft-delete a version. GUARD: refuses to delete the LAST live version
 * of the concept. Deleting the active version reassigns active to the first
 * remaining live version. Returns a NEW concepts array.
 */
export function softDeleteVersion(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  versionId: string,
  deletedAt: string = new Date().toISOString(),
): DesignConcept[] {
  return mapConceptVersions(concepts, conceptId, (versions) => {
    const live = versions.filter((v) => v.deleted !== true);
    if (live.length <= 1) return versions; // never delete the last version
    const target = live.find((v) => v.versionId === versionId);
    if (!target) return versions;
    const wasActive = target.isActive === true;
    const remaining = live.filter((v) => v.versionId !== versionId);
    return versions.map((v) => {
      if (v.versionId === versionId)
        return { ...v, deleted: true, deletedAt, isActive: false };
      if (wasActive && remaining.length > 0)
        return { ...v, isActive: v.versionId === remaining[0].versionId };
      return v;
    });
  });
}

/** PURE — clear the soft-delete flag on a version (Undo / Trash restore). */
export function restoreVersion(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  versionId: string,
): DesignConcept[] {
  return mapConceptVersions(concepts, conceptId, (versions) =>
    versions.map((v) => {
      if (v.versionId !== versionId) return v;
      const { deleted: _d, deletedAt: _da, ...rest } = v;
      return rest;
    }),
  );
}

/** PURE — hard-remove a version by id (Trash purge). */
export function purgeVersion(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  versionId: string,
): DesignConcept[] {
  return mapConceptVersions(concepts, conceptId, (versions) =>
    versions.filter((v) => v.versionId !== versionId),
  );
}

/**
 * PURE — apply a mapping fn to ONE version's floors[] (by conceptId+versionId).
 * The reusable spine for all floor-level edits inside the hierarchy.
 */
function mapVersionFloors(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  versionId: string,
  fn: (floors: PropertyFloor[]) => PropertyFloor[],
): DesignConcept[] {
  return mapConceptVersions(concepts, conceptId, (versions) =>
    versions.map((v) =>
      v.versionId === versionId ? { ...v, floors: fn(v.floors ?? []) } : v,
    ),
  );
}

/**
 * PURE — add a floor to a concept+version. No-op (returns input concepts) when a
 * LIVE floor with that floorId already exists in the version. A previously
 * soft-deleted floor of the same id is restored (and replaced) rather than
 * duplicated. Returns a NEW concepts array.
 */
export function addFloorToVersion(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  versionId: string,
  floor: PropertyFloor,
): DesignConcept[] {
  return mapVersionFloors(concepts, conceptId, versionId, (floors) => {
    const live = floors.find((f) => f.floorId === floor.floorId && f.deleted !== true);
    if (live) return floors; // already present, don't duplicate
    const deletedIdx = floors.findIndex((f) => f.floorId === floor.floorId);
    if (deletedIdx >= 0) {
      // Re-add a previously-deleted floor: replace it (clears the flag).
      return floors.map((f, i) => (i === deletedIdx ? { ...floor } : f));
    }
    return [...floors, { ...floor }];
  });
}

/** PURE — rename a floor's label within a concept+version. */
export function renameFloor(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  versionId: string,
  floorId: string,
  label: string,
): DesignConcept[] {
  return mapVersionFloors(concepts, conceptId, versionId, (floors) =>
    floors.map((f) => (f.floorId === floorId ? { ...f, label } : f)),
  );
}

/** PURE — soft-delete a floor within a concept+version (never splice). */
export function softDeleteFloor(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  versionId: string,
  floorId: string,
  deletedAt: string = new Date().toISOString(),
): DesignConcept[] {
  return mapVersionFloors(concepts, conceptId, versionId, (floors) =>
    floors.map((f) =>
      f.floorId === floorId ? { ...f, deleted: true, deletedAt } : f,
    ),
  );
}

/** PURE — clear the soft-delete flag on a floor (Undo / Trash restore). */
export function restoreFloor(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  versionId: string,
  floorId: string,
): DesignConcept[] {
  return mapVersionFloors(concepts, conceptId, versionId, (floors) =>
    floors.map((f) => {
      if (f.floorId !== floorId) return f;
      const { deleted: _d, deletedAt: _da, ...rest } = f;
      return rest;
    }),
  );
}

/** PURE — hard-remove a floor within a concept+version (Trash purge). */
export function purgeFloor(
  concepts: DesignConcept[] | undefined,
  conceptId: string,
  versionId: string,
  floorId: string,
): DesignConcept[] {
  return mapVersionFloors(concepts, conceptId, versionId, (floors) =>
    floors.filter((f) => f.floorId !== floorId),
  );
}

// ─── AD-038 — Trash: collect soft-deleted nested items (PURE) ─────────────

/**
 * A soft-deleted nested item (concept / version / floor) surfaced in Trash. Not
 * a separate doc — it lives inside `properties/{propertyId}.concepts[…]`, so the
 * ref carries the full path needed to restore (clear flag) or purge (hard-remove).
 */
export interface DeletedPropertyChild {
  kind: 'concept' | 'version' | 'floor';
  propertyId: string;
  conceptId: string;
  versionId?: string;
  floorId?: string;
  /** Human label for the Trash row (e.g. "Concept B", "v2 · Developed", "GF · Ground Floor"). */
  label: string;
  /** ISO timestamp the item was soft-deleted (for sort + "trashed <when>"). */
  deletedAt?: string;
}

/**
 * PURE — walk a property's concepts[] and collect every soft-deleted nested item
 * (concept, version, floor). A deleted CONCEPT is surfaced as one row (its
 * deleted versions/floors ride with it — restoring the concept brings them back);
 * we do not also list children of an already-deleted concept. Within a LIVE
 * concept, deleted versions are surfaced; within a LIVE version, deleted floors.
 */
export function collectDeletedChildren(
  prop: Pick<LiveProperty, 'propertyId' | 'concepts'>,
): DeletedPropertyChild[] {
  const out: DeletedPropertyChild[] = [];
  const pid = prop.propertyId;
  for (const c of prop.concepts ?? []) {
    if (c.deleted === true) {
      out.push({ kind: 'concept', propertyId: pid, conceptId: c.conceptId, label: c.name, deletedAt: c.deletedAt });
      continue; // its versions/floors ride with the concept
    }
    for (const v of c.versions ?? []) {
      if (v.deleted === true) {
        out.push({ kind: 'version', propertyId: pid, conceptId: c.conceptId, versionId: v.versionId, label: `${v.versionId} · ${v.stage}`, deletedAt: v.deletedAt });
        continue;
      }
      for (const f of v.floors ?? []) {
        if (f.deleted === true) {
          out.push({ kind: 'floor', propertyId: pid, conceptId: c.conceptId, versionId: v.versionId, floorId: f.floorId, label: `${f.floorId} · ${f.label}`, deletedAt: f.deletedAt });
        }
      }
    }
  }
  return out;
}

// ─── Seed shape passed to createProperty() ───────────────────────────────

export type PropertySeed = Pick<
  LiveProperty,
  'address' | 'projectType' | 'northRotation' | 'siteLocation' | 'addressComps'
>;

// ─── Pure helpers ─────────────────────────────────────────────────────────

/**
 * PURE — return the city component of a property's address for list display.
 * Prefers addressComps.city, falls back to parsing the last significant
 * comma-separated segment of the address string.
 */
export function propertyCityLabel(prop: LiveProperty): string {
  if (prop.addressComps?.city) return prop.addressComps.city;
  if (!prop.address) return '';
  const parts = prop.address.split(',').map((s) => s.trim()).filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

/**
 * PURE — convert DMS components to a signed decimal degree.
 * dir 'S' or 'W' negates the result.
 */
export function dmsToDecimal(
  deg: number,
  min: number,
  sec: number,
  dir: 'N' | 'S' | 'E' | 'W',
): number {
  const d = deg + min / 60 + sec / 3600;
  return dir === 'S' || dir === 'W' ? -d : d;
}

/**
 * PURE — format a decimal degree as a DMS string for display.
 * isLat=true → N/S suffix; isLat=false → E/W suffix.
 */
export function decimalToDms(decimal: number, isLat: boolean): string {
  const abs = Math.abs(decimal);
  const deg = Math.floor(abs);
  const minFloat = (abs - deg) * 60;
  const min = Math.floor(minFloat);
  const sec = ((minFloat - min) * 60).toFixed(3);
  const dir = isLat
    ? decimal >= 0 ? 'N' : 'S'
    : decimal >= 0 ? 'E' : 'W';
  return `${deg}° ${min}′ ${sec}″ ${dir}`;
}

/**
 * PURE — validate that a PropertySeed has at least one of the mandatory
 * location inputs (Google Maps URL or coordinates). Address alone is not enough.
 */
export function hasRequiredLocation(seed: PropertySeed): boolean {
  const sl = seed.siteLocation;
  if (!sl) return false;
  const hasGoogle = Boolean(sl.mapsUrl?.trim());
  const hasCoords = sl.lat != null && sl.lng != null;
  return hasGoogle || hasCoords;
}

/**
 * PURE — build the first PropertyFloor entry from a FloorId.
 * Returns a Phase-2 shape with empty `plans[]`.
 */
export function makePropertyFloor(id: FloorId, label?: string): PropertyFloor {
  return { floorId: id, label: (label && label.trim()) || FLOOR_LABELS[id], plans: [] };
}

/**
 * PURE — resolve a TYPED floor/surface name (creatable combobox, AD-048) to a
 * registry { floorId, label }:
 *   · an exact preset label  → its canonical FloorId + canonical label;
 *   · a custom label (e.g. "Site / plot map") → a FREE FloorId slot (prefer the
 *     floorIdFromLabel heuristic when that slot is free, else the first unused
 *     slot) + the typed label PRESERVED. The grid reads the boundary, not the
 *     floorId, so a custom-labelled floor grids correctly.
 * `used` = FloorIds already taken on the property/version.
 */
export function resolveFloorChoice(
  typed: string,
  used: readonly string[] = [],
): { floorId: FloorId; label: string } {
  const t = (typed || '').trim();
  // exact preset label (case-insensitive)?
  const presetId = FLOOR_IDS.find((id) => FLOOR_LABELS[id].toLowerCase() === t.toLowerCase());
  if (presetId) return { floorId: presetId, label: FLOOR_LABELS[presetId] };
  const usedSet = new Set(used);
  // custom label → a free slot. Prefer the heuristic mapping if its slot is free.
  const heuristic = floorIdFromLabel(t);
  if (!usedSet.has(heuristic)) return { floorId: heuristic, label: t || FLOOR_LABELS[heuristic] };
  const free = FLOOR_IDS.find((id) => !usedSet.has(id));
  return { floorId: (free ?? heuristic), label: t || FLOOR_LABELS[heuristic] };
}

/**
 * PURE — mint a plan version id from the current epoch (simple + sortable).
 * e.g. `v_1718700000000`.
 */
export function newPlanId(): string {
  return `v_${Date.now()}`;
}

// ─── Grid snapshot types (Section 3.4 + Phase 2 spec Section 3) ──────────

/**
 * Per-zone summary stored on a grid snapshot. The engine's canonical 16-zone
 * names (e.g. 'NE', 'N', 'NNW', …) are the keys.
 */
export interface ZoneSnapshot {
  zone: string;
  /** Zone area as a fraction of total boundary area (0–1). */
  areaFraction?: number;
  /** Centroid of the zone polygon in image-pixel space. */
  centroid?: Pt2D;
  /** Panchatattva element label ('Space'|'Air'|'Fire'|'Water'|'Earth'). */
  element?: string;
}

/**
 * Per-pada summary stored on a grid snapshot. Each pada is a 32-radial
 * sector (11.25° wide). The `label` is the canonical code ('N1'–'N8', etc.).
 */
export interface PadaSnapshot {
  label: string;
  zone: string;
  /** Centre bearing (degrees from true north). */
  bearing?: number;
  /** Pada centroid in image-pixel space. */
  centroid?: Pt2D;
  /** Devta name for this pada (from the 45-devta Vastu Purush Mandala). */
  devta?: string;
}

/**
 * A persisted grid snapshot stored at `properties/{pid}/grids/{gridId}`.
 *
 * The `gridId` is constructed by `buildGridId(floorId, planVersionId, version)`.
 * Only one grid per floor is `isActive === true`; the engine reads the active
 * grid when `answers.gridId` + `answers.propertyId` are set (the GRID_ENGINE_SEAM).
 *
 * `zones[]` and `padas[]` are the per-zone and per-pada summaries derived from
 * the engine's full computation. The engine JSON blob is optional (kept for
 * round-trip fidelity but not required by the report seam).
 */
export interface GridSnapshot {
  /** e.g. "GF_v_1718700000000_v1" — built by `buildGridId`. */
  gridId: string;
  /** The floor this grid was computed from. */
  floorId: string;
  /** The plan version id whose boundary was used (`FloorPlanVersion.planId`). */
  planVersionId: string;
  /** Version counter (1 = first computed grid for this floor+plan). */
  version: number;
  /** True-north bearing used for this computation. */
  northRotation: number;
  /** Boundary polygon in image-pixel space (same as `PropertyFloor.boundary`). */
  boundary?: Pt2D[];
  /** Per-zone summaries (16 zones). */
  zones?: ZoneSnapshot[];
  /** Per-pada summaries (32 padas). */
  padas?: PadaSnapshot[];
  /** Full engine JSON blob (optional — large, kept for audit). */
  engineJson?: unknown;
  /** Engine version string (e.g. 'v6'). */
  engineVersion?: string;
  /** Sha256 hash of (boundary + northRotation) — used for cache comparison. */
  boundaryHash?: string;
  /** ISO timestamp of computation. */
  computedAt: string;
  /** UID of the user who triggered this computation. */
  computedBy?: string;
  /** Is this the current active grid for its floor? Only one is true at a time. */
  isActive: boolean;
}

// ─── Pure helpers for grid IDs and plan activation ───────────────────────

/**
 * PURE — build a deterministic gridId.
 *
 * AD-013 (legacy) keyed a grid by `floorId + planVersionId + version`:
 *   `"${floorId}_${planVersionId}_v${version}"` e.g. `"GF_v_1718700000000_v1"`.
 *
 * AD-038 closes the multi-concept grid gap: a grid is now ALSO keyed by the
 * concept + version it belongs to, so `ConceptA·v2·GF` ≠ `ConceptB·v1·GF`. When
 * `conceptId` + `versionId` are supplied the id is prefixed:
 *   `"cg::${conceptId}::${versionId}::${floorId}_${planVersionId}_v${version}"`.
 *
 * The legacy 3-arg call site (single-concept) still produces the OLD bare id, so
 * existing grid docs keep resolving unchanged. `parseGridId` reads both shapes.
 */
export function buildGridId(
  floorId: string,
  planVersionId: string,
  version: number,
  conceptId?: string,
  versionId?: string,
): string {
  const base = `${floorId}_${planVersionId}_v${version}`;
  if (conceptId != null && versionId != null) {
    return `${GRID_ID_PREFIX}${conceptId}${GRID_ID_SEP}${versionId}${GRID_ID_SEP}${base}`;
  }
  return base;
}

/** AD-038 — prefix + separator for the concept-aware gridId form. */
export const GRID_ID_PREFIX = 'cg::';
export const GRID_ID_SEP = '::';

/** Parsed components of a gridId — back-compat for both old and new shapes. */
export interface ParsedGridId {
  /** Present only on AD-038 concept-aware ids; undefined for legacy ids. */
  conceptId?: string;
  /** Present only on AD-038 concept-aware ids; undefined for legacy ids. */
  versionId?: string;
  floorId: string;
  planVersionId: string;
  version: number;
  /** The original (legacy) `${floorId}_${planVersionId}_v${version}` tail. */
  base: string;
}

/**
 * PURE — parse a gridId built by `buildGridId`. Reads BOTH the AD-038
 * concept-aware form (`cg::concept::version::base`) AND the legacy bare base
 * (`GF_v_1718700000000_v1`), so old grid docs still resolve. Returns null when
 * the base tail is unrecognisable.
 */
export function parseGridId(gridId: string): ParsedGridId | null {
  let conceptId: string | undefined;
  let versionId: string | undefined;
  let base = gridId;
  if (gridId.startsWith(GRID_ID_PREFIX)) {
    const rest = gridId.slice(GRID_ID_PREFIX.length);
    const parts = rest.split(GRID_ID_SEP);
    // [conceptId, versionId, ...baseSegmentsThatHeldNo'::'] — base never holds '::'.
    if (parts.length < 3) return null;
    conceptId = parts[0];
    versionId = parts[1];
    base = parts.slice(2).join(GRID_ID_SEP);
  }
  // base = `${floorId}_${planVersionId}_v${version}`. version = trailing `_v<digits>`.
  const m = base.match(/^(.*)_v(\d+)$/);
  if (!m) return null;
  const head = m[1]; // `${floorId}_${planVersionId}`
  const version = Number(m[2]);
  const us = head.indexOf('_');
  if (us < 0) return null;
  const floorId = head.slice(0, us);
  const planVersionId = head.slice(us + 1);
  return { conceptId, versionId, floorId, planVersionId, version, base };
}

/**
 * PURE — return the active `FloorPlanVersion` for a floor's `plans[]` array.
 * Falls back to `plans[0]` when none is explicitly active, or `null` when
 * the array is empty.
 */
export function activeFloorPlan(floor: PropertyFloor): FloorPlanVersion | null {
  const plans = floor.plans ?? [];
  if (plans.length === 0) return null;
  const found = plans.find((v) => v.isActive === true);
  return found ?? plans[0];
}

/**
 * PURE — append a new FloorPlanVersion to a floor's `plans[]`, deactivating all
 * prior versions and marking the new one active. Returns a NEW array (never
 * mutates). The newcomer's `isActive` is forced true regardless of its input
 * flag, so the result always has exactly one active version (the appended one).
 *
 * This is the canonical "add a revised drawing" operation (AD-036 drawing-version
 * bridge: whole-set or single-floor). The optional `stage` label rides on the
 * version itself (`FloorPlanVersion.stage`).
 */
export function appendPlanVersion(
  plans: FloorPlanVersion[] | undefined,
  version: FloorPlanVersion,
): FloorPlanVersion[] {
  const deactivated = (plans ?? []).map((v) =>
    v.isActive ? { ...v, isActive: false } : v,
  );
  return [...deactivated, { ...version, isActive: true }];
}

/**
 * PURE — ensure exactly one `FloorPlanVersion` in the array has
 * `isActive === true`. Rules:
 *   - If exactly one is already active → return the array unchanged.
 *   - If zero are active → return a new array with `plans[0].isActive = true`.
 *   - If more than one are active → deactivate all except the first active one.
 *
 * Returns a NEW array (never mutates the input).
 */
export function enforceActivePlan(
  plans: FloorPlanVersion[],
): FloorPlanVersion[] {
  if (plans.length === 0) return plans;
  const activeCount = plans.filter((v) => v.isActive === true).length;
  if (activeCount === 1) return plans;
  if (activeCount === 0) {
    return plans.map((v, i) => (i === 0 ? { ...v, isActive: true } : v));
  }
  // More than one active — keep first active, deactivate the rest.
  let foundFirst = false;
  return plans.map((v) => {
    if (v.isActive && !foundFirst) {
      foundFirst = true;
      return v;
    }
    return v.isActive ? { ...v, isActive: false } : v;
  });
}
